import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ISocketOrderEvent } from '@/types/ISocketOrderEvent';
import { type ICallPayload } from '@/types/ICallPayload';
import { type INotification } from '@/types/INotification';
import { isNewerOrderEvent } from '@/utils/isNewerOrderEvent';
import { createOrderNotification } from '@/utils/createOrderNotification';
import { createCallNotification } from '@/utils/createCallNotification';
import { isCallNotification } from '@/utils/isCallNotification';
import { isSameLocalDay } from '@/utils/isSameLocalDay';
import {
  getNotificationsHistory,
  addNotificationToHistory,
  markNotificationRead,
  markOrderNotificationRead,
  markAllNotificationsRead,
  clearNotificationsHistory
} from '@/services/notifications/notificationsHistoryStorage';

export interface INotificationsState {
  /** Эфемерный слой: видимые тосты (авто-скрытие, не персистится). */
  toasts: INotification[];
  /** Постоянная история для панели — зеркало localStorage. */
  history: INotification[];
  /** Раскрыт ли схлопнутый тост-сводка (>2 тостов). */
  isExpanded: boolean;
  /** Последнее обработанное событие заказа — для дедупа более новых. */
  lastEvent: ISocketOrderEvent | null;
}

const initialState: INotificationsState = {
  toasts: [],
  history: getNotificationsHistory(),
  isExpanded: false,
  lastEvent: null
};

const notifications = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    enqueueOrderNotification(state, action: PayloadAction<ISocketOrderEvent>) {
      const event = action.payload;

      if (!isNewerOrderEvent(event, state.lastEvent)) {
        return;
      }

      state.lastEvent = event;

      const notification = createOrderNotification(event);
      if (state.history.some((item) => item.id === notification.id)) {
        return;
      }

      state.toasts.push(notification);
      state.history = addNotificationToHistory(notification);
    },
    /**
     * Кладёт заказ только в эфемерный слой тостов, не наполняя историю.
     * Центр уведомлений выведен из работы: под него будет отдельный сокет и
     * отдельный сервис. `enqueueOrderNotification` остаётся в слайсе рядом —
     * для отката и для будущего сервиса.
     */
    enqueueOrderToast(state, action: PayloadAction<ISocketOrderEvent>) {
      const event = action.payload;

      if (!isNewerOrderEvent(event, state.lastEvent)) {
        return;
      }

      state.lastEvent = event;

      const notification = createOrderNotification(event);

      // Дедуп по видимой очереди: раньше эту роль играла история, которую мы
      // больше не наполняем.
      if (state.toasts.some((item) => item.id === notification.id)) {
        return;
      }

      state.toasts.push(notification);
    },
    /**
     * Кладёт звонок в общий стек тостов. Upsert по `call-<phone>`: повторное
     * событие по тому же номеру обновляет карточку (перезапуская её таймер
     * жизни через новый `occurredAt`), а не добавляет вторую. Историю звонки
     * не наполняют — они живут только в эфемерном слое.
     */
    enqueueCallToast(state, action: PayloadAction<ICallPayload>) {
      const notification = createCallNotification(action.payload);
      const index = state.toasts.findIndex(
        (item) => item.id === notification.id
      );

      if (index === -1) {
        state.toasts.push(notification);
        return;
      }

      state.toasts[index] = notification;
    },
    /** Скрывает тост, не затрагивая историю (запись остаётся в панели). */
    dismissToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter((item) => item.id !== action.payload);
    },
    /**
     * Гасит карточку звонка, только если это карточка ВХОДЯЩЕГО.
     * Карточку пропущенного не трогает: живое терминальное событие может
     * прийти позже записи в историю, и безусловное гашение убирало бы уже
     * показанное — и законное — уведомление о пропущенном звонке.
     */
    dismissIncomingCallToast(state, action: PayloadAction<string>) {
      state.toasts = state.toasts.filter(
        (item) =>
          !(
            item.id === action.payload &&
            isCallNotification(item) &&
            item.payload.source === 'incoming'
          )
      );
    },
    clearToasts(state) {
      state.toasts = [];
      state.isExpanded = false;
    },
    /**
     * Закрывает только тосты заказов — кнопка «Закрыть все» на схлопнутом
     * стеке. Карточки звонков живут отдельной группой, не схлопываются и этой
     * кнопке не подчиняются.
     */
    clearOrderToasts(state) {
      state.toasts = state.toasts.filter(isCallNotification);
      state.isExpanded = false;
    },
    setExpanded(state, action: PayloadAction<boolean>) {
      state.isExpanded = action.payload;
    },
    markRead(state, action: PayloadAction<string>) {
      state.history = markNotificationRead(action.payload);
    },
    /** Помечает прочитанным уведомление заказа по uuid (открытие заказа). */
    markOrderRead(state, action: PayloadAction<string>) {
      state.history = markOrderNotificationRead(action.payload);
    },
    markAllRead(state) {
      state.history = markAllNotificationsRead();
    },
    /** Очистка истории пользователем (кнопка «Очистить» в панели). Дедуп
     *  (`lastEvent`) сохраняется, чтобы уже показанное событие не повторилось. */
    clearHistory(state) {
      clearNotificationsHistory();
      state.history = [];
    },
    /** Полная очистка тостов и истории (логаут). */
    clearAll(state) {
      clearNotificationsHistory();
      state.toasts = [];
      state.history = [];
      state.isExpanded = false;
      state.lastEvent = null;
    },
    /**
     * Логаут: историю НЕ удаляем (переживает перелогин в рамках дня), но
     * помечаем всё прочитанным, чтобы счётчик/favicon не поднимались от старых
     * записей. Эфемерное (тосты, раскрытие, дедуп) сбрасываем. Суточная чистка
     * сама уберёт ленту через день.
     */
    markAllReadOnLogout(state) {
      state.history = markAllNotificationsRead();
      state.toasts = [];
      state.isExpanded = false;
      state.lastEvent = null;
    },
    /** Ленивая суточная чистка: оставляет записи текущего локального дня. */
    pruneStale(state) {
      const now = Date.now();
      state.history = state.history.filter((item) =>
        isSameLocalDay(item.createdAt, now)
      );
    },
    /** Ресинхронизация истории с localStorage (другая вкладка, событие storage). */
    syncHistory(state) {
      state.history = getNotificationsHistory();
    }
  }
});

export const {
  enqueueOrderNotification,
  enqueueOrderToast,
  enqueueCallToast,
  dismissToast,
  dismissIncomingCallToast,
  clearToasts,
  clearOrderToasts,
  setExpanded,
  markRead,
  markOrderRead,
  markAllRead,
  clearHistory,
  clearAll,
  markAllReadOnLogout,
  pruneStale,
  syncHistory
} = notifications.actions;

export default notifications.reducer;
